import "./global.scss";
import { MetaProvider, Title } from "@solidjs/meta";
import { Router, useLocation, useNavigate, useRoutes } from "@solidjs/router";
import { QueryClientProvider } from "@tanstack/solid-query";
import type { Component } from "solid-js";
import { createEffect, lazy, onCleanup, onMount, Suspense } from "solid-js";
import { queryClient } from "~/solidJs/shared/api/queryClient";
import { restoreUtms } from "~/solidJs/shared/helpers/amplitudeHelpers";
import { AxiosClientHoc } from "~/solidJs/shared/helpers/HOCS/AxiosClientHoc";
import { ConfigHoc } from "~/solidJs/shared/helpers/HOCS/ConfigHOC";
import { SuperTokens } from "~/solidJs/shared/helpers/HOCS/SuperTokens";
import { SuspenseBlock } from "~/solidJs/shared/ui/atoms/SuspenseBlock/SuspenseBlock";
import { ErrorBoundary } from "~/solidJs/shared/ui/organisms/ErrorBoundary";
import { WorkspaceToaster } from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";
import { oldRedirectMap, routes } from "../models/routes";

type LayoutTrainPanelApp = Record<string, never>;

export const App: Component<LayoutTrainPanelApp> = (_props) => {
	const Routes = useRoutes(routes);

	return (
		<ErrorBoundary>
			<PreventDefaultMacZoom />
			<WorkspaceToaster />
			<MetaProvider>
				<Router>
					<SuspenseBlock size="large">
						<ConfigHoc>
							<AxiosClientHoc>
								<SuperTokens>
									<QueryClientProvider client={queryClient}>
										<SuspenseBlock name="App" size="large">
											<Routes />
											<GlobalPageTitle />
											<TrackRedirectToNew />
											<ShowQueryDevTools />
										</SuspenseBlock>
									</QueryClientProvider>
								</SuperTokens>
							</AxiosClientHoc>
						</ConfigHoc>
					</SuspenseBlock>
				</Router>
			</MetaProvider>
		</ErrorBoundary>
	);
};

const ShowQueryDevTools = () => {
	if (window.location.hostname !== "localhost") {
		return null;
	}
	const importDevTools = () => {
		const module = import("@tanstack/solid-query-devtools");
		return module.then(({ SolidQueryDevtools }) => ({ default: SolidQueryDevtools }));
	};
	return <Suspense>{lazy(importDevTools)({})}</Suspense>;
};

const GlobalPageTitle = () => {
	const cleanOriginalTag = () => {
		for (const tag of document.querySelectorAll("title")) {
			tag.remove();
		}
	};
	cleanOriginalTag();
	return <Title>Phygital+</Title>;
};

const TrackRedirectToNew = () => {
	const navigate = useNavigate();

	const routerLocation = useLocation();
	createEffect(() => {
		const pathUrl = new URL(routerLocation.pathname, window.location.origin);
		if (pathUrl.pathname in oldRedirectMap) {
			const params = new URLSearchParams(restoreUtms());
			navigate(`${oldRedirectMap[pathUrl.pathname]}?${params}`);
		}
	});
	return null;
};

const PreventDefaultMacZoom = () => {
	const preventDefaultMacZoom = (e: WheelEvent) => {
		if (e.ctrlKey && e.deltaY <= 0) {
			e.preventDefault();
		}
	};

	const preventDefaultWindowsZoom = (e: WheelEvent) => {
		if (e.ctrlKey) {
			e.preventDefault();
		}
	};

	onMount(() => {
		if (window.location.hostname === "localhost") return;
		if (navigator.userAgent.indexOf("Mac") !== -1) {
			document.body.addEventListener("wheel", preventDefaultMacZoom, { passive: false });
		} else {
			document.body.addEventListener("wheel", preventDefaultWindowsZoom, { passive: false });
		}
	});
	onCleanup(() => {
		if (navigator.userAgent.indexOf("Mac") !== -1) {
			document.body.removeEventListener("wheel", preventDefaultMacZoom);
		} else {
			document.body.removeEventListener("wheel", preventDefaultWindowsZoom);
		}
	});
	return null;
};
